import React from 'react';
import { useTranslation } from 'react-i18next';

// Single-country (Zambia) phone input. Shows a fixed "+26" prefix chip so the
// user types their number EXACTLY as they say it locally — e.g. 0775722228 —
// and the saved value becomes "+26" + "0775722228" = "+260775722228" (E.164).
// Backwards-compatible: values already stored with a non-Zambian "+xxx" prefix
// fall back to a plain editable input.

const PREFIX = '+26';
const ZAMBIA_E164 = '+260';

// Split a stored value into the "local" portion that goes in the text input.
// Stored values come in three shapes that all need to render gracefully:
//   "+260775722228"  -> local "0775722228"   (the leading 0 trunk prefix is
//                                             always shown back to the user)
//   "775722228"      -> local "0775722228"   (legacy: no prefix at all)
//   "0775722228"     -> local "0775722228"
function toLocal(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  if (v.startsWith(ZAMBIA_E164)) return '0' + v.slice(ZAMBIA_E164.length);
  if (/^\d+$/.test(v)) return v.startsWith('0') ? v : '0' + v;
  return '';
}

function isZambian(value) {
  const v = String(value || '').trim();
  return !v || v.startsWith(ZAMBIA_E164) || /^\d+$/.test(v);
}

const PhoneInput = ({ value, onChange, placeholder, disabled, required }) => {
  const { t } = useTranslation();

  // Fallback path for legacy non-Zambian numbers (+251..., +44..., etc).
  // Kept editable so the admin can correct it, but no prefix chip.
  if (!isZambian(value)) {
    return (
      <input
        type="tel"
        value={value || ''}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        placeholder="+code number"
        style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border, #e5e7eb)', borderRadius: 8, fontSize: 14, outline: 'none' }}
      />
    );
  }

  const local = toLocal(value);
  const looksValid = !local || /^0\d{9}$/.test(local);

  const onLocalChange = (raw) => {
    let cleaned = String(raw || '').replace(/\D/g, '');
    // If user pasted a full number like "260775722228" or "+260775722228",
    // strip the country code so we don't double-prefix on save.
    if (cleaned.startsWith('260')) cleaned = '0' + cleaned.slice(3);
    cleaned = cleaned.slice(0, 10);
    if (!cleaned) onChange('');
    // The saved value is PREFIX("+26") + local("0xxxxxxxxx") — which is the
    // same 13-char E.164 string as Zambia's "+260" + 9-digit national number.
    else onChange(PREFIX + cleaned);
  };

  return (
    <div>
      <div style={{ display: 'flex' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 12px', minWidth: 56,
          background: '#f3f4f6',
          border: `1px solid ${looksValid ? 'var(--border, #d1d5db)' : '#fca5a5'}`,
          borderRight: 'none', borderRadius: '8px 0 0 8px',
          color: '#374151', fontSize: 14, fontWeight: 600, userSelect: 'none',
        }}>
          {PREFIX}
        </span>
        <input
          type="tel"
          inputMode="numeric"
          maxLength={10}
          value={local}
          disabled={disabled}
          required={required}
          onChange={e => onLocalChange(e.target.value)}
          placeholder={placeholder || '0977123456'}
          style={{
            flex: 1, padding: '10px 12px',
            border: `1px solid ${looksValid ? 'var(--border, #d1d5db)' : '#fca5a5'}`,
            borderRadius: '0 8px 8px 0', fontSize: 14, outline: 'none',
            minWidth: 0,
          }}
        />
      </div>
      {local && !looksValid && (
        <small style={{ color: '#dc2626', fontSize: 11, display: 'block', marginTop: 4 }}>
          {t('phoneInput.invalid', { defaultValue: 'Number must be 10 digits starting with 0' })}
        </small>
      )}
    </div>
  );
};

export default PhoneInput;
